import type { Company, Site } from '@/data/schemas';

function normalizeSequence(sequence?: number | null): number | null {
  if (!sequence || !Number.isFinite(sequence)) return null;
  const normalized = Math.floor(sequence);
  return normalized > 0 ? normalized : null;
}

export function formatSiteSequence(sequence?: number | null): string | null {
  const normalized = normalizeSequence(sequence);
  if (!normalized) return null;
  const digits = Math.max(String(normalized).length, 2);
  return `S${String(normalized).padStart(digits, '0')}`;
}

export function formatSiteShorthand(
  company: Pick<Company, 'code'> | null | undefined,
  site: Pick<Site, 'sequenceNumber'> | null | undefined
): string | null {
  if (!site) return null;
  const sequence = formatSiteSequence(site.sequenceNumber);
  if (!sequence) return null;
  const companyCode = company?.code?.trim();
  return companyCode ? `${companyCode}/${sequence}` : sequence;
}

export function resolveSiteDisplayLabel(
  company: Pick<Company, 'code'> | null | undefined,
  site: Pick<Site, 'name' | 'sequenceNumber'> | null | undefined
): { name: string; shorthand: string | null } {
  const rawName = site?.name?.trim() || '';
  const isUnitPlaceholder =
    rawName.length > 0 &&
    (rawName.toLowerCase() === 'impianto' || /^impianto\s+\d+$/i.test(rawName));
  const fallbackLabel =
    site?.sequenceNumber && Number.isFinite(site.sequenceNumber)
      ? `Stabilimento ${site.sequenceNumber}`
      : 'Stabilimento';
  const defaultLabel = isUnitPlaceholder ? fallbackLabel : rawName || 'Stabilimento';
  return {
    name: defaultLabel,
    shorthand: formatSiteShorthand(company, site ?? undefined),
  };
}

export function formatUnitDisplayLabel(name: string | null | undefined, index?: number): string {
  const fallback = index !== undefined ? `Impianto ${index + 1}` : 'Impianto';
  const trimmed = name?.trim() ?? '';
  if (!trimmed) return fallback;
  if (trimmed.includes('{{') || trimmed.includes('}}')) return fallback;

  const lower = trimmed.toLowerCase();
  if (lower.startsWith('impianto')) return trimmed;

  const stripped = trimmed.replace(/^centrale\s*/i, '').trim();
  if (!stripped) return fallback;
  if (stripped.toLowerCase().startsWith('impianto')) return stripped;
  return `Impianto ${stripped}`;
}
